import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Search from "@insite/mobius/Icons/Search";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    searchTextField?: TextFieldPresentationProps;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                // max-height: 350px;
                max-height: 300px;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                padding-bottom: 5px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
            weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    searchTextField: {
        iconProps: {
            src: Search,
            css: css`
                position: absolute;
                box-sizing: border-box;
                top: 2px;
                right: 2px;
                width: 36px;
                height: 36px;
                padding: 8px;
                border-radius: 100%;
                pointer-events: none;
            `,
        },
        cssOverrides: {
            formField: css`
                width: 100%;
                > div[class^="FormInputWrapper"] {
                    > input {
                        background: #f1f1f1;
                        border: 0px;
                        border-bottom: 2px solid #ccc;
                        border-radius: 0px;
                        margin-bottom: 8px;
                    }
                }
            `,
        },
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);

    // const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const limitedFacets = expanded ? facets : facets?.slice(0, facets.length);
    const [tempResult, setTempResult] = useState(limitedFacets);
    const anySelected = facets.find(f => f.selected) !== undefined;
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        setTempResult(limitedFacets);
    }, [facets]);

    const doSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (searchText.trim() !== "") {
            const templimitedFacets = limitedFacets.filter(element =>
                element.name.toLowerCase().includes(searchText.toLowerCase()),
            );
            setTempResult(templimitedFacets);
        } else {
            setTempResult(limitedFacets);
        }
    };

    const queryStyling = (name: string) => {
        if (searchText.trim() === "") {
            return name;
        }
        const queryArray = searchText ? name.toLowerCase().match(searchText.toLowerCase()) : null;
        const arr1: string[] = [];
        if (searchText && queryArray) {
            const queryIndex = queryArray.index ? queryArray.index : 0;
            arr1.push(name.substr(0, queryIndex));
            arr1.push("<span style='font-weight:800'>");
            arr1.push(name.substr(queryIndex, searchText.length));
            arr1.push("</span>");
            arr1.push(name.substr(queryIndex + searchText.length));
        }
        return arr1.join("");
    };

    return (
        <ManagedAccordionSection
            title={title}
            {...styles.accordionSection}
            initialExpanded={expandByDefault || anySelected}
        >
            <TextField
                {...styles.searchTextField}
                value={searchText}
                onChange={event => setSearchText(event.currentTarget.value)}
                onKeyUp={doSearch}
                placeholder={translate(`Enter keywords`)}
                type="text"
                data-test-selector="productListSearchWithin"
                id="productsKeywordSearch"
            />
            {tempResult?.map(facet => (
                <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                    <Checkbox
                        {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                        checked={facet.selected}
                        onChange={() => onChangeFacet(facet)}
                        data-test-selector={`facetFilter${facet.id}`}
                    >
                        <Typography
                            id="label"
                            css={css`
                                text-transform: uppercase;
                                font-size: 14px;
                                > span {
                                    padding-right: 5px;
                                }
                            `}
                        >
                            <span
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: queryStyling(facet.name),
                                }}
                            ></span>
                            {facet.count !== -1 && `(${facet.count})`}
                        </Typography>
                    </Checkbox>
                </CheckboxGroup>
            ))}
        </ManagedAccordionSection>
    );
};

export default ProductListFiltersAccordionSection;
